import { Stack, styled, Theme } from "@mui/material";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";

import Notification, {
  Props as NotificationProps,
} from "../components/notification";

type ToastNotification = Pick<
  NotificationProps,
  "severity" | "message" | "title" | "autoHideDuration"
>;

type ToastNotificationServiceContextType = {
  createToastNotification: (notification: ToastNotification) => {
    close: () => void;
  };
};

const ToastNotificationServiceContext = createContext<
  ToastNotificationServiceContextType | undefined
>(undefined);

const ToastNotificationServiceProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [toastNotification, setToastNotification] = useState<
    ({
      id: number;
    } & ToastNotification)[]
  >([]);

  const handleClose = useCallback((id: number): void => {
    setToastNotification((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  }, []);

  const createToastNotification = useCallback(
    (notification: ToastNotification): { close: () => void } => {
      const id = Date.now();

      setToastNotification((prev) => [
        ...prev,
        {
          id,
          ...notification,
        },
      ]);

      return {
        close: () => handleClose(id),
      };
    },
    [handleClose],
  );

  // Stable across toast changes: consumers list the service as a hook
  // dependency, and a new one on every toast re-renders them all.
  const value = useMemo(
    () => ({ createToastNotification }),
    [createToastNotification],
  );

  return (
    <ToastNotificationServiceContext.Provider value={value}>
      {children}
      <NotificationStack spacing={2} data-testid="notification-stack">
        {toastNotification?.map(({ id, autoHideDuration, ...actions }) => (
          <Notification
            key={id}
            autoHideDuration={autoHideDuration || 6000}
            onClose={() => handleClose(id)}
            {...actions}
          />
        ))}
      </NotificationStack>
    </ToastNotificationServiceContext.Provider>
  );
};

// Pinned to the bottom-left corner above everything else so toasts never
// scroll away with the page.
const NotificationStack = styled(Stack)(({ theme }: { theme: Theme }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  left: theme.spacing(3),
  right: "auto",
  zIndex: 9999,
}));

export {
  ToastNotificationServiceContext,
  ToastNotificationServiceContextType,
  ToastNotificationServiceProvider,
};
